'use client';

import { useState, useRef, useEffect } from 'react';
import { ChevronDown, Plus } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { PartGroup } from '@/services/api/part-groups';
import { useTranslation } from '@/hooks/useTranslation';

interface GroupMultiSelectProps {
  groups: PartGroup[];
  selectedGroupIds: string[];
  onSelectionChange: (groupIds: string[]) => void;
}

export function GroupMultiSelect({
  groups,
  selectedGroupIds,
  onSelectionChange,
}: GroupMultiSelectProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const toggleGroup = (groupId: string) => {
    // 单选模式：直接设置为当前选中的分组，并关闭下拉框
    onSelectionChange([groupId]);
    setIsOpen(false);
  };

  const handleCreateGroup = () => {
    setIsOpen(false);
    // 跳转到分组管理页面
    router.push('/parts/attributes?action=create');
  };
  
  const getDisplayText = () => {
    if (selectedGroupIds.length === 0) {
      return t.parts.pleaseSelectGroupPlaceholder;
    }
    
    // 单选模式：只显示一个分组名称
    const groupId = selectedGroupIds[0];
    const group = groups.find(g => g.id === groupId);
    return group ? group.name : t.parts.pleaseSelectGroupPlaceholder;
  };
  
  return (
    <div className="relative" ref={dropdownRef} data-group-selector>
      {/* 标签 */}
      <label htmlFor="group-validation-input" className="block text-sm font-medium text-gray-700 mb-1">
        {t.parts.selectGroup}
        <span className="text-red-500 ml-1">*</span>
      </label>
      
      {/* 触发按钮 */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full min-w-[200px] px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <span className={selectedGroupIds.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
          {getDisplayText()}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      
      {/* 下拉选项 */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-[300px] overflow-auto">
          {/* 创建分组选项 */}
          <div
            onClick={handleCreateGroup}
            className="flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-200 text-blue-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            <span className="text-sm font-medium">{t.parts.createGroup || 'Create Group'}</span>
          </div>

          {/* 分组选项 */}
          {groups.filter(g => g.isActive).map(group => (
            <div
              key={group.id}
              onClick={() => toggleGroup(group.id)}
              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
            >
              <div className="flex items-center justify-center w-4 h-4 mr-2 border border-gray-300 rounded-full">
                {selectedGroupIds.includes(group.id) && (
                  <div className="w-2 h-2 bg-blue-600 rounded-full" />
                )}
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-900">{group.name}</div>
                {group.fieldsCount > 0 && (
                  <div className="text-xs text-gray-500">
                    {t.parts.fieldsCount.replace('{count}', group.fieldsCount.toString())}
                  </div>
                )}
              </div>
            </div>
          ))}
          
          {/* 无可用分组 */}
          {groups.filter(g => g.isActive).length === 0 && (
            <div className="px-3 py-6 text-center text-sm text-gray-500">
              {t.parts.noAvailableGroups}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

